import { useState } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { NotificationBell } from "./NotificationBell";
import { SignOut, List, X, Package, PlusCircle, Truck, IdentificationCard, ChartBar, UsersThree, MapTrifold, CurrencyDollar, Buildings, CalendarCheck, ArrowsClockwise, Envelope } from "@phosphor-icons/react";

const LINKS = {
  CLIENT: [
    { to: "/app/client", label: "Mis fletes", icon: Package },
    { to: "/app/client/publish", label: "Publicar flete", icon: PlusCircle },
    { to: "/app/client/agenda", label: "Mi agenda", icon: CalendarCheck },
    { to: "/app/client/company", label: "Mi Empresa", icon: Buildings },
    { to: "/app/returns", label: "Retornos", icon: ArrowsClockwise },
  ],
  CARRIER: [
    { to: "/app/carrier", label: "Oportunidades", icon: Truck },
    { to: "/app/carrier/services", label: "Mis servicios", icon: Package },
    { to: "/app/carrier/agenda", label: "Mi agenda", icon: CalendarCheck },
    { to: "/app/carrier/onboarding", label: "Mi acreditación", icon: IdentificationCard },
    { to: "/app/carrier/company", label: "Mi Empresa", icon: Buildings },
    { to: "/app/carrier/vehicles", label: "Vehículos", icon: Truck },
    { to: "/app/carrier/drivers", label: "Conductores", icon: UsersThree },
    { to: "/app/carrier/earnings", label: "Ganancias", icon: CurrencyDollar },
    { to: "/app/returns", label: "Retornos", icon: ArrowsClockwise },
  ],
  ADMIN: [
    { to: "/app/admin", label: "Panel", icon: ChartBar },
    { to: "/app/admin?tab=carriers", label: "Transportistas", icon: Truck },
    { to: "/app/admin?tab=users", label: "Usuarios", icon: UsersThree },
    { to: "/app/admin?tab=freights", label: "Fletes", icon: Package },
    { to: "/app/admin?tab=messages", label: "Mensajes", icon: Envelope },
    { to: "/app/admin/agenda", label: "Agenda global", icon: CalendarCheck },
    { to: "/app/returns", label: "Retornos", icon: ArrowsClockwise },
    { to: "/app/admin?tab=map", label: "Mapa", icon: MapTrifold },
  ],
};

export function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  let links = LINKS[user?.role] || [];
  if (user?.role === "CARRIER" && user?.parentCarrierId) {
    const perms = user.permissions || [];
    const PATH_PERM = {
      "/app/carrier": "opportunities",
      "/app/carrier/services": "services",
      "/app/carrier/agenda": "agenda",
      "/app/carrier/onboarding": "accreditation",
      "/app/carrier/company": "company",
      "/app/carrier/vehicles": "vehicles",
      "/app/carrier/drivers": "drivers",
      "/app/carrier/earnings": "earnings",
    };
    links = links.filter((l) => perms.includes(PATH_PERM[l.to]));
  }
  if (user?.role === "CLIENT" && user?.parentCarrierId) {
    const perms = user.permissions || [];
    const PP = {
      "/app/client": "freights",
      "/app/client/publish": "publish",
      "/app/client/agenda": "agenda",
      "/app/client/company": "company",
    };
    links = links.filter((l) => l.to === "/app/returns" || perms.includes(PP[l.to]));
  }
  const home = links[0]?.to || "/";

  const NavList = ({ onNavigate }) => (
    <nav className="flex flex-col gap-1" data-testid="sidebar-nav">
      {links.map((l) => {
        const current = loc.pathname + loc.search;
        const active = current === l.to || (!l.to.includes("?") && loc.pathname === l.to && !loc.search);
        return (
          <Link key={l.to} to={l.to} onClick={onNavigate} data-testid={`side-${l.label}`}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-colors ${active ? "bg-[#FF5A36] text-white" : "text-zinc-600 hover:bg-zinc-100"}`}>
            <l.icon size={20} weight={active ? "fill" : "regular"} /> {l.label}
          </Link>
        );
      })}
    </nav>
  );

  const UserFooter = () => (
    <div className="flex items-center justify-between px-2 pt-4 mt-4 border-t border-zinc-200">
      <div className="min-w-0">
        <div className="text-sm font-semibold truncate">{user?.name}</div>
        <div className="text-[10px] uppercase tracking-[0.15em] text-[#FF5A36] font-bold">{user?.role}</div>
      </div>
      <button data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }}
        className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-zinc-100 transition-colors active:scale-95">
        <SignOut size={20} />
      </button>
    </div>
  );

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-[#F8F9FA]">
      {/* Full-width header */}
      <header className="shrink-0 z-40 h-16 bg-white border-b border-zinc-200 flex items-stretch">
        <Link to={home} className="hidden lg:flex w-64 shrink-0 items-center justify-center border-r border-zinc-200">
          <img src="/frets-logo.png" alt="Frets Transporte" className="h-9 w-auto" />
        </Link>
        <div className="flex-1 flex items-center px-4">
          <button onClick={() => setOpen(true)} data-testid="mobile-menu-btn" className="lg:hidden w-10 h-10 flex items-center justify-center"><List size={24} /></button>
          <Link to={home} className="lg:hidden ml-1"><img src="/frets-logo.png" alt="Frets" className="h-8 w-auto" /></Link>
          <div className="ml-auto flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-2" data-testid="header-user">
              <div className="text-sm font-semibold text-zinc-800 truncate max-w-[180px]" data-testid="header-user-name">{user?.name}</div>
              <div className="w-9 h-9 rounded-full bg-[#FF5A36]/10 text-[#FF5A36] flex items-center justify-center text-sm font-bold shrink-0">{(user?.name || "?").charAt(0).toUpperCase()}</div>
            </div>
            <NotificationBell />
          </div>
        </div>
      </header>

      {/* Body: sidebar + content */}
      <div className="flex-1 flex min-h-0">
        <aside className="hidden lg:flex w-64 shrink-0 bg-white border-r border-zinc-200 flex-col p-5 overflow-y-auto">
          <NavList />
        </aside>
        <main className="flex-1 min-w-0 overflow-y-auto"><Outlet /></main>
      </div>

      {/* Full-width footer: DoSoft */}
      <footer className="shrink-0 bg-white border-t border-zinc-200 flex items-stretch" data-testid="app-footer">
        <div className="hidden lg:flex w-64 shrink-0 items-center justify-between px-5 py-3 border-r border-zinc-200">
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">{user?.name}</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#FF5A36] font-bold">{user?.role}</div>
          </div>
          <button data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }}
            className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-zinc-100 transition-colors active:scale-95">
            <SignOut size={20} />
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center py-4 text-sm text-zinc-500" data-testid="footer-info">© {new Date().getFullYear()} DoSoft</div>
      </footer>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-white p-5 flex flex-col">
            <div className="flex items-center justify-between mb-8">
              <img src="/frets-logo.png" alt="Frets" className="h-8 w-auto" />
              <button onClick={() => setOpen(false)} className="w-10 h-10 flex items-center justify-center"><X size={24} /></button>
            </div>
            <div className="flex-1"><NavList onNavigate={() => setOpen(false)} /></div>
            <UserFooter />
          </div>
        </div>
      )}
    </div>
  );
}
